import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, ReactNode } from "react";
import { useARC } from "./ARC/ARCProvider";
import { useMissionControl } from "./navigation/MissionProvider";
import { ConnectionState } from "lib/consts/ARC/connection";
import { getActionCallerBySocket } from "lib/utils/connection";
import { ActionHandle, ActionNotSupportedError } from "arcjs/action/base";

const STAIRS_ACTION_NAME = "/navigate_stairs";

const GOAL_ID_STORAGE_KEY = "stairs_action_goal_id";
const ACTION_TIMEOUT_MS = 30000; // 30 seconds - if no status update, consider action stuck

/** Matches stairs_detection/action/NavigateStairs goal (direction, landing, levels, correction_mode). */
interface StairData {
    direction: string;
    landing: number;
    levels: number;
    correction_mode?: string;
}

interface ActionResult {
    error?: string;
    [key: string]: unknown;
}

interface ActionFeedback {
    [key: string]: unknown;
}

interface StairsContextType {
    actionStatus: string | null;
    actionResult: ActionResult | null;
    actionFeedback: ActionFeedback | null;
    goalId: string | null;
    showActionFeedback: boolean;
    isActionValid: boolean;
    setShowActionFeedback: (show: boolean) => void;
    handleStairConfirm: (stairData: StairData) => void;
    handleActionCancel: () => void;
    handleForceClose: () => void;
}

const StairsContext = createContext<StairsContextType | undefined>(undefined);
StairsContext.displayName = "Stairs";

export const useStairs = (): StairsContextType => {
    const context = useContext(StairsContext);
    if (!context) {
        throw new Error("useStairs must be used within StairsProvider");
    }
    return context;
};

interface StairsProviderProps {
    children: ReactNode;
}

const StairsProvider = ({ children }: StairsProviderProps) => {
    const { controlSocket, connectionState } = useARC();
    const { missionRecording, missionRecordCallback } = useMissionControl();
    const actionCaller = useMemo(
        () => getActionCallerBySocket(controlSocket),
        [controlSocket],
    );

    const [actionStatus, setActionStatus] = useState<string | null>(null);
    const [actionResult, setActionResult] = useState<ActionResult | null>(null);
    const [actionFeedback, setActionFeedback] = useState<ActionFeedback | null>(null);
    const [goalId, setGoalId] = useState<string | null>(null); // canonical UUID (display + status/timeout keying)
    const handleRef = useRef<ActionHandle | null>(null);
    const [showActionFeedback, setShowActionFeedback] = useState(false);
    const [isActionValid, setIsActionValid] = useState(true);

    // Track last status update time for timeout detection
    const lastStatusUpdateTime = useRef<number | null>(null);
    const statusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Reset action state when connection is lost or forced close
    const resetActionState = useCallback(() => {
        console.log("[StairsProvider] Resetting action state");
        setActionStatus(null);
        setActionResult(null);
        setActionFeedback(null);
        setGoalId(null);
        handleRef.current = null;
        setIsActionValid(false);
        setShowActionFeedback(false);
        localStorage.removeItem(GOAL_ID_STORAGE_KEY);
        if (statusTimeoutRef.current) {
            clearTimeout(statusTimeoutRef.current);
            statusTimeoutRef.current = null;
        }
        lastStatusUpdateTime.current = null;
    }, []);

    // Cancel the active action
    const handleActionCancel = useCallback(() => {
        console.log("[StairsProvider] Cancel requested", {
            hasHandle: !!handleRef.current,
            goalId,
            connectionState,
            isActionValid,
        });

        // Step 1: Clear persistent storage to prevent reload from resurrecting the action
        localStorage.removeItem(GOAL_ID_STORAGE_KEY);

        // Step 2: If not connected, action invalid, or we no longer hold the goal
        // handle (e.g. after a refresh on a backend that can't re-attach), just clear
        // local state.
        if (
            connectionState !== ConnectionState.Connected ||
            !isActionValid ||
            !handleRef.current
        ) {
            console.warn("[StairsProvider] Cannot cancel on server; clearing stairs session");
            resetActionState();
            return;
        }

        // Step 3: Connected & valid - cancel on the server. The cancellation result
        // arrives via the onFailed callback (STATUS_CANCELED), which updates status.
        try {
            void handleRef.current.cancel();
            console.log("[StairsProvider] cancel() sent. Waiting for onFailed with canceled...");
        } catch (error) {
            console.error("[StairsProvider] Error while cancelling (will still clear local session):", error);
            resetActionState();
        }
    }, [connectionState, isActionValid, resetActionState, goalId]);

    // Force close action dialog (for stuck/invalid actions)
    const handleForceClose = useCallback(() => {
        console.log("[StairsProvider] Force closing action dialog");
        resetActionState();
    }, [resetActionState]);

    const handleStairConfirm = useCallback(
        (stairData: StairData) => {
            // If in mission recording mode, call the recording callback instead of executing
            if (missionRecording && missionRecordCallback) {
                console.log("[StairsProvider] Mission recording mode - calling record callback");
                missionRecordCallback(stairData);
                return;
            }

            if (!actionCaller) {
                console.error("Action control source not available");
                setIsActionValid(false);
                return;
            }

            const goalMessage = {
                direction: stairData.direction,
                landing: stairData.landing,
                levels: stairData.levels,
                correction_mode: stairData.correction_mode ?? "vision",
            };

            console.log("Sending navigate_stairs action goal:", goalMessage);

            // Optimistic active state; the goal id + handle land when sendGoal resolves.
            setActionStatus("active");
            setActionFeedback(null);
            setActionResult(null);
            setShowActionFeedback(true);
            setIsActionValid(true);
            lastStatusUpdateTime.current = Date.now();

            actionCaller
                .sendGoal<typeof goalMessage, ActionFeedback, ActionResult>(
                    STAIRS_ACTION_NAME,
                    goalMessage,
                    {
                        onResult: (result) => {
                            setActionStatus("succeeded");
                            setActionResult(result as ActionResult);
                            localStorage.removeItem(GOAL_ID_STORAGE_KEY);
                            lastStatusUpdateTime.current = Date.now();
                            if (statusTimeoutRef.current) {
                                clearTimeout(statusTimeoutRef.current);
                                statusTimeoutRef.current = null;
                            }
                        },
                        onFeedback: (feedback) => {
                            setActionFeedback(feedback as ActionFeedback);
                            lastStatusUpdateTime.current = Date.now();
                            setIsActionValid(true);
                        },
                        onFailed: (error) => {
                            console.log("[StairsProvider] onFailed:", error);
                            const errorStr = String(error);
                            const lowerError = errorStr.toLowerCase();
                            if (
                                lowerError.includes("canceled") ||
                                lowerError.includes("status_canceled") ||
                                lowerError.includes("was canceled")
                            ) {
                                setActionStatus("canceled");
                            } else {
                                setActionStatus("failed");
                            }
                            setActionResult({ error: errorStr });
                            localStorage.removeItem(GOAL_ID_STORAGE_KEY);
                            lastStatusUpdateTime.current = Date.now();
                            if (statusTimeoutRef.current) {
                                clearTimeout(statusTimeoutRef.current);
                                statusTimeoutRef.current = null;
                            }
                        },
                    },
                )
                .then((handle) => {
                    handleRef.current = handle;
                    setGoalId(handle.goalId);
                    localStorage.setItem(GOAL_ID_STORAGE_KEY, handle.goalId);
                    console.log("[StairsProvider] goal accepted, id:", handle.goalId);
                })
                .catch((error) => {
                    console.error("Failed to send navigate_stairs action goal:", error);
                    setIsActionValid(false);
                    setActionStatus("failed");
                    setActionResult({ error: String(error) });
                });
        },
        [actionCaller, missionRecording, missionRecordCallback]
    );

    // Note: Status/feedback/result are now handled via Action class callbacks in handleStairConfirm
    // No separate ActionListener needed for ROS 2 - this follows ROS 2 best practices

    // Handle disconnection
    useEffect(() => {
        if (connectionState === ConnectionState.Disconnected && goalId) {
            console.warn("[StairsProvider] Connection lost during active action");
            setIsActionValid(false);
            setActionStatus("failed");
            setActionResult({ error: "Connection lost. Action may be invalid. Please restart after reconnecting." });
        }
    }, [connectionState, goalId]);

    // Timeout for stuck actions
    useEffect(() => {
        if (!goalId || actionStatus !== "active") {
            if (statusTimeoutRef.current) {
                clearTimeout(statusTimeoutRef.current);
                statusTimeoutRef.current = null;
            }
            lastStatusUpdateTime.current = null;
            return;
        }

        if (!lastStatusUpdateTime.current) {
            lastStatusUpdateTime.current = Date.now();
        }

        statusTimeoutRef.current = setTimeout(() => {
            const elapsed = Date.now() - (lastStatusUpdateTime.current || Date.now());
            if (elapsed >= ACTION_TIMEOUT_MS && actionStatus === "active") {
                console.warn(`[StairsProvider] Action timeout: ${elapsed}ms`);
                setIsActionValid(false);
                setActionStatus("failed");
                setActionResult({
                    error: `Action appears stuck (no status update for ${Math.round(elapsed / 1000)}s). The action server may be unavailable.`
                });
            }
        }, ACTION_TIMEOUT_MS);

        return () => {
            if (statusTimeoutRef.current) {
                clearTimeout(statusTimeoutRef.current);
                statusTimeoutRef.current = null;
            }
        };
    }, [goalId, actionStatus]);

    // On mount: restore from localStorage (page refresh). Re-attaching to a goal
    // started in a previous session requires monitor-by-id, which only the Foxglove
    // source supports; the roslib source throws ActionNotSupportedError → we mark the
    // session invalid and offer manual close (roslib could never truly re-attach).
    useEffect(() => {
        const storedGoalId = localStorage.getItem(GOAL_ID_STORAGE_KEY);
        if (!storedGoalId || handleRef.current) return;

        // New writes store the canonical UUID; tolerate a legacy full rosbridge id.
        const uuidValue = storedGoalId.includes(":")
            ? storedGoalId.split(":").pop() ?? storedGoalId
            : storedGoalId;

        if (!actionCaller || connectionState !== ConnectionState.Connected) {
            console.warn("Stored goal ID found but connection not ready. Marking invalid.");
            setGoalId(uuidValue);
            setIsActionValid(false);
            setActionStatus("failed");
            setActionResult({ error: "Connection not available. Previous action may be invalid." });
            setShowActionFeedback(true);
            return;
        }

        console.log("Restoring stairs action from page refresh. Stored Goal ID:", uuidValue);
        setGoalId(uuidValue);
        setActionStatus("active");
        setShowActionFeedback(true);
        setIsActionValid(true);
        lastStatusUpdateTime.current = Date.now();

        actionCaller
            .monitorGoal<ActionFeedback, ActionResult>(STAIRS_ACTION_NAME, uuidValue, {
                onResult: (result) => {
                    setActionStatus("succeeded");
                    setActionResult(result as ActionResult);
                    localStorage.removeItem(GOAL_ID_STORAGE_KEY);
                    lastStatusUpdateTime.current = Date.now();
                },
                onFeedback: (feedback) => {
                    setActionFeedback(feedback as ActionFeedback);
                    lastStatusUpdateTime.current = Date.now();
                    setIsActionValid(true);
                },
                onFailed: (error) => {
                    const lowerError = String(error).toLowerCase();
                    setActionStatus(lowerError.includes("cancel") ? "canceled" : "failed");
                    setActionResult({ error: String(error) });
                    localStorage.removeItem(GOAL_ID_STORAGE_KEY);
                    lastStatusUpdateTime.current = Date.now();
                },
            })
            .then((handle) => {
                handleRef.current = handle;
            })
            .catch((error) => {
                if (error instanceof ActionNotSupportedError) {
                    console.warn("[StairsProvider] Backend cannot re-attach to a prior goal; marking invalid.");
                } else {
                    console.error("[StairsProvider] Failed to re-attach to stored goal:", error);
                }
                setIsActionValid(false);
                setActionStatus("failed");
                setActionResult({
                    error: "Previous action can't be resumed. Close and restart if needed.",
                });
                setShowActionFeedback(true);
            });
    }, [actionCaller, connectionState]);

    const value: StairsContextType = {
        actionStatus,
        actionResult,
        actionFeedback,
        goalId,
        showActionFeedback,
        isActionValid,
        setShowActionFeedback,
        handleStairConfirm,
        handleActionCancel,
        handleForceClose,
    };

    return <StairsContext.Provider value={value}>{children}</StairsContext.Provider>;
};

export default StairsProvider;
